import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Music, Disc3, Sliders, Radio, Folder, AudioWaveform, 
  Zap, Target, Repeat, Play, Volume2, Activity 
} from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-500 via-transparent to-transparent animate-pulse"></div>
        </div>

        {/* Header */}
        <header className="relative z-10 container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Disc3 className="w-8 h-8 text-white animate-spin" style={{animationDuration: '3s'}} />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  Harmonic Mixer
                </h1>
                <p className="text-xs text-gray-400">Professional DJ Software</p>
              </div>
            </div>
            <div className="flex gap-3">
              {localStorage.getItem('token') ? (
                <>
                  <Button onClick={() => window.location.href = '/dashboard'} className="bg-cyan-600 hover:bg-cyan-700">
                    Dashboard
                  </Button>
                  <Button onClick={() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.reload();
                  }} variant="outline" className="border-gray-600 hover:bg-gray-800">
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => window.location.href = '/login'} variant="outline" className="border-gray-600 hover:bg-gray-800">
                    Login
                  </Button>
                  <Button onClick={() => window.location.href = '/signup'} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 py-20 text-center">
          <Badge className="bg-purple-600/30 text-purple-300 border-purple-500/50 mb-6">
            ✨ Professional-Grade DJ Software
          </Badge>
          
          <h2 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Mix Like a Pro
            <br />
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              In Perfect Harmony
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Professional DJ mixer with harmonic mixing, MIDI controller support, 
            audio effects, and intelligent BPM/key detection. Everything you need 
            to create seamless mixes.
          </p>

          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              onClick={() => window.location.href = '/mixer'} 
              size="lg" 
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-lg px-8 py-6"
            >
              <Music className="w-5 h-5 mr-2" />
              Start DJing Now
            </Button>
            <Button 
              onClick={() => window.location.href = '/library'} 
              size="lg" 
              variant="outline" 
              className="border-purple-500/50 bg-purple-900/20 hover:bg-purple-900/40 text-lg px-8 py-6"
            >
              <Folder className="w-5 h-5 mr-2" />
              Manage Library
            </Button>
          </div>

          <div className="mt-8 flex gap-6 justify-center text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-green-400" />
              Web-Based
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              Zero Latency
            </div>
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-purple-400" />
              MIDI Ready
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h3 className="text-4xl font-bold mb-4">Powerful Features</h3>
          <p className="text-gray-400 text-lg">Everything a professional DJ needs</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              icon: <Disc3 className="w-8 h-8" />,
              title: "Dual-Deck Mixing",
              description: "Professional 2-deck mixer with crossfader, EQ, and volume controls",
              color: "cyan"
            },
            {
              icon: <Target className="w-8 h-8" />,
              title: "Harmonic Mixing",
              description: "Automatic BPM and key detection with Camelot wheel notation",
              color: "purple"
            },
            {
              icon: <Radio className="w-8 h-8" />,
              title: "MIDI Controller Support",
              description: "Full MIDI learn mode for any hardware controller (Pioneer, Rane, etc.)",
              color: "pink"
            },
            {
              icon: <Zap className="w-8 h-8" />,
              title: "Real-Time Effects",
              description: "Echo, reverb, and filter effects with Web Audio API",
              color: "yellow"
            },
            {
              icon: <AudioWaveform className="w-8 h-8" />,
              title: "Waveform Visualization",
              description: "Pre-rendered waveforms with real-time playhead tracking",
              color: "green"
            },
            {
              icon: <Folder className="w-8 h-8" />,
              title: "Smart Library",
              description: "Virtual crates, external drive support, and duplicate detection",
              color: "blue"
            },
            {
              icon: <Activity className="w-8 h-8" />,
              title: "Spectrum Analyzer",
              description: "Real-time frequency spectrum visualization per deck",
              color: "orange"
            },
            {
              icon: <Volume2 className="w-8 h-8" />,
              title: "VU Meters",
              description: "Professional volume level monitoring with peak indicators",
              color: "red"
            },
            {
              icon: <Repeat className="w-8 h-8" />,
              title: "Hot Cues & Loops",
              description: "Set hot cue points and create seamless loops on the fly",
              color: "indigo"
            }
          ].map((feature, idx) => (
            <Card key={idx} className="bg-gray-900/50 border-gray-800 hover:border-purple-500/50 transition-all">
              <CardContent className="p-6">
                <div className={`w-16 h-16 bg-${feature.color}-600/20 rounded-lg flex items-center justify-center text-${feature.color}-400 mb-4`}>
                  {feature.icon}
                </div>
                <h4 className="text-xl font-bold mb-2">{feature.title}</h4>
                <p className="text-gray-400">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Tech Stack */}
      <div className="container mx-auto px-4 py-20">
        <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-2xl border border-purple-500/30 p-12 text-center">
          <h3 className="text-3xl font-bold mb-6">Built with Modern Technology</h3>
          <div className="flex flex-wrap gap-4 justify-center">
            {['React', 'FastAPI', 'MongoDB', 'Web Audio API', 'MIDI API', 'Electron Ready'].map(tech => (
              <Badge key={tech} className="bg-purple-600/30 text-purple-300 border-purple-500/50 px-4 py-2">
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-4 py-20 text-center">
        <h3 className="text-4xl md:text-5xl font-bold mb-6">
          Ready to Start Mixing?
        </h3>
        <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
          Launch Harmonic Mixer now and experience professional-grade DJing 
          with zero installation required.
        </p>
        <Button 
          onClick={() => window.location.href = '/mixer'} 
          size="lg" 
          className="bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600 hover:from-cyan-700 hover:via-purple-700 hover:to-pink-700 text-xl px-12 py-8"
        >
          <Play className="w-6 h-6 mr-3" />
          Launch Harmonic Mixer
        </Button>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Disc3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold">Harmonic Mixer</p>
                <p className="text-xs text-gray-500">Professional DJ Software</p>
              </div>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-sm text-gray-400">
                © {new Date().getFullYear()} Gary Hall
              </p>
              <p className="text-xs text-gray-500">
                DJ DJSlidin Daversatile • Producer • Artist
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
